import { useMemo, useCallback } from "react";
import type { BaseEntity, SerializedViewState } from "../../../UniversalCRMTable/types";
import type { ConfigWithEnhancedBulkActions } from "@/lib/types/bulk-actions-enhanced";
import { convertSavedViewToGraphQLFilter } from "@/lib/services/filter-conversion";

interface UseCompositionContextPartsParams<T extends BaseEntity> {
  dataState: any;
  config: ConfigWithEnhancedBulkActions<T>;
  entityName: string;
}

/**
 * Extracts the stable sub-memos from useCompositionContextValue to keep
 * that file under the 300-line limit. No functional changes.
 *
 * Returns:
 * - dataStateWithoutLoading: data fields that should NOT trigger context recreation on load
 * - stableSetSerializeFilters / stableSetApplyFilters: stable noop callbacks
 * - convertToGraphQLCallback: stable filter conversion callback
 */
export function useCompositionContextParts<T extends BaseEntity>({
  dataState,
  config,
  entityName,
}: UseCompositionContextPartsParams<T>) {
  // CRITICAL: Separate data state from loading state.
  // Loading state changes should NOT trigger context value recreation.
  // This prevents full page remounts when search results are loading.
  const dataStateWithoutLoading = useMemo(
    () => ({
      data: dataState.data,
      error: dataState.error,
      refetch: dataState.refetch,
      processedData: dataState.processedData,
      normalizedData: dataState.data,
      groupableFields: dataState.groupableFields,
      hasLoadedOnce: dataState.hasLoadedOnce,
      ftsIsLoading: dataState.ftsIsLoading,
      ftsError: dataState.ftsError,
      hasNextPage: dataState.hasNextPage,
      isFetchingNextPage: dataState.isFetchingNextPage,
      loadMore: dataState.loadMore,
    }),
    [
      dataState.data,
      dataState.error,
      dataState.refetch,
      dataState.processedData,
      dataState.groupableFields,
      dataState.hasLoadedOnce,
      dataState.ftsIsLoading,
      dataState.ftsError,
      dataState.hasNextPage,
      dataState.isFetchingNextPage,
      dataState.loadMore,
    ]
  );

  // Stable noops for fields that have no real implementation — avoids creating new function
  // references inside the useMemo body, which would make consumers that dep on them re-render.
  const stableSetSerializeFilters = useCallback(() => {}, []);
  const stableSetApplyFilters = useCallback(() => {}, []);

  // Memoize the convertToGraphQL callback separately to keep it stable
  const convertToGraphQLCallback = useCallback(
    (serializedState: SerializedViewState) => {
      return convertSavedViewToGraphQLFilter(serializedState, config, entityName);
    },
    [config, entityName]
  );

  return {
    dataStateWithoutLoading,
    stableSetSerializeFilters,
    stableSetApplyFilters,
    convertToGraphQLCallback,
  };
}
